import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Star, Flame, Zap, Crown, Shield, Award, Lock, ChevronRight } from 'lucide-react';

/**
 * Gamification Preview Section
 * Shows badges, levels, and tiers with unlock effects
 */
const GamificationPreview = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'en';
  const [hoveredBadge, setHoveredBadge] = useState(null);
  
  const badges = [
    { id: 1, name: t('gamificationPreview.badges.initiate'), icon: Star, color: '#a3e635', unlocked: true, description: t('gamificationPreview.badges.initiateDesc') },
    { id: 2, name: t('gamificationPreview.badges.warrior'), icon: Flame, color: '#f97316', unlocked: true, description: t('gamificationPreview.badges.warriorDesc') },
    { id: 3, name: t('gamificationPreview.badges.athlete'), icon: Zap, color: '#06b6d4', unlocked: true, description: t('gamificationPreview.badges.athleteDesc') },
    { id: 4, name: t('gamificationPreview.badges.master'), icon: Crown, color: '#eab308', unlocked: false, description: t('gamificationPreview.badges.masterDesc') },
    { id: 5, name: t('gamificationPreview.badges.elite'), icon: Shield, color: '#a855f7', unlocked: false, description: t('gamificationPreview.badges.eliteDesc') },
    { id: 6, name: t('gamificationPreview.badges.legend'), icon: Award, color: '#ec4899', unlocked: false, description: t('gamificationPreview.badges.legendDesc') },
  ];
  
  const tiers = [
    { name: t('gamificationPreview.tiers.bronze'), color: '#cd7f32', progress: 100, current: false },
    { name: t('gamificationPreview.tiers.silver'), color: '#c0c0c0', progress: 100, current: false },
    { name: t('gamificationPreview.tiers.gold'), color: '#ffd700', progress: 65, current: true },
    { name: t('gamificationPreview.tiers.platinum'), color: '#e5e4e2', progress: 0, current: false },
    { name: t('gamificationPreview.tiers.diamond'), color: '#b9f2ff', progress: 0, current: false },
  ];
  
  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#eab308]/10 border border-[#eab308]/30 mb-4">
          <Trophy className="w-4 h-4 text-[#eab308]" />
          <span className="text-[#eab308] text-sm font-medium">GAMIFICATION</span>
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
          {t('gamificationPreview.titlePart1')} <span className="text-[#a3e635]">{t('gamificationPreview.titleBadges')}</span> {t('gamificationPreview.titlePart2')} <span className="text-[#eab308]">{t('gamificationPreview.titleUp')}</span>
        </h3>
        <p className="text-gray-400 text-sm">
          {t('gamificationPreview.subtitle')}
        </p>
      </div>
      
      {/* Badges Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 mb-10">
        {badges.map((badge) => {
          const Icon = badge.icon;
          const isHovered = hoveredBadge === badge.id;
          
          return (
            <div
              key={badge.id}
              className="relative group cursor-pointer"
              onMouseEnter={() => setHoveredBadge(badge.id)}
              onMouseLeave={() => setHoveredBadge(null)}
            >
              {/* Badge circle */}
              <div 
                className={`relative w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full flex items-center justify-center
                          border-2 transition-all duration-300
                          ${badge.unlocked 
                            ? 'bg-black shadow-lg' 
                            : 'bg-zinc-900/50 opacity-50'}`}
                style={{ 
                  borderColor: badge.unlocked ? badge.color : '#444',
                  boxShadow: badge.unlocked && isHovered ? `0 0 30px ${badge.color}60` : undefined,
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                {badge.unlocked ? (
                  <Icon 
                    className="w-7 h-7 sm:w-8 sm:h-8 transition-all duration-300"
                    style={{ 
                      color: badge.color,
                      filter: isHovered ? `drop-shadow(0 0 10px ${badge.color})` : undefined,
                    }}
                  />
                ) : (
                  <Lock className="w-6 h-6 text-zinc-600" />
                )}
                
                {/* Unlock effect */}
                {badge.unlocked && isHovered && (
                  <div 
                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: badge.color }}
                  />
                )}
              </div>
              
              {/* Badge name */}
              <p className={`text-center text-xs mt-2 font-medium transition-colors
                          ${badge.unlocked ? 'text-white' : 'text-zinc-600'}`}>
                {badge.name}
              </p>
              
              {/* Tooltip */}
              {isHovered && badge.unlocked && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-40 p-2
                              bg-black/95 rounded-lg border text-center z-50"
                     style={{ borderColor: `${badge.color}60` }}>
                  <p className="text-xs text-gray-300">{badge.description}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tier Progress */}
      <div className="bg-black/50 rounded-2xl p-6 border border-[#a3e635]/20">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-semibold">{t('gamificationPreview.tierSystem')}</h4>
          <span className="text-[#eab308] text-sm font-medium">{t('gamificationPreview.goldLevel')} - 65%</span>
        </div>
        
        {/* Tier bar */}
        <div className="relative h-8 bg-zinc-900 rounded-full overflow-hidden mb-4">
          {tiers.map((tier, index) => {
            const width = 100 / tiers.length;
            const left = index * width;
            
            return (
              <div
                key={tier.name}
                className="absolute top-0 bottom-0 flex items-center justify-center
                         border-r border-zinc-700 last:border-r-0"
                style={{ 
                  left: `${left}%`, 
                  width: `${width}%`,
                  background: tier.progress > 0 
                    ? `linear-gradient(90deg, ${tier.color}40 0%, ${tier.color}20 ${tier.progress}%, transparent ${tier.progress}%)`
                    : 'transparent',
                }}
              >
                <span className={`text-xs font-medium ${tier.current ? 'text-white' : 'text-zinc-500'}`}>
                  {tier.name}
                </span>
              </div>
            );
          })}
          
          {/* Progress indicator */}
          <div 
            className="absolute top-0 bottom-0 w-1 bg-[#a3e635] shadow-[0_0_10px_#a3e635]"
            style={{ left: `${(2 * 20) + (20 * 0.65)}%` }}
          />
        </div>
        
        {/* CTA */}
        <div className="flex items-center justify-center gap-2 text-[#a3e635] text-sm font-medium
                      cursor-pointer hover:text-[#39FF14] transition-colors">
          <span>{t('gamificationPreview.unlockMore')}</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

export default GamificationPreview;
